// The box state machine as data (03 §3.6). box.ts interprets this table; nothing else
// decides which scans are legal.

import type {
  BoxEvent,
  BoxEventType,
  BoxExceptionType,
  BoxStatus,
  PartnerStatus,
} from './box-types';
import { BOX_STATUSES, isFinalStatus } from './box-types';

/**
 * Refusal preconditions are business rules: when they fail, the command is refused and nothing
 * changes. Physical preconditions describe where the box should be: when they fail the scan is
 * still true, so the box follows it and an exception opens.
 */
export const REFUSAL_PRECONDITIONS = [
  'paid_in_full',
  'no_active_hold',
  'no_blocking_hold',
  'returning_flag',
  'shipment_not_exported',
] as const;
export const PHYSICAL_PRECONDITIONS = [
  'at_event_branch',
  'same_carrier',
  'scan_at_destination',
] as const;

export type RefusalPrecondition = (typeof REFUSAL_PRECONDITIONS)[number];
export type PhysicalPrecondition = (typeof PHYSICAL_PRECONDITIONS)[number];
export type Precondition = RefusalPrecondition | PhysicalPrecondition;

export interface TransitionRow {
  readonly from: BoxStatus | 'none';
  readonly event: BoxEventType;
  readonly to: BoxStatus;
  readonly preconditions: readonly Precondition[];
  /** `partnerUpdate` only: the row applies to these partner statuses. */
  readonly partnerStatuses?: readonly PartnerStatus[];
  /** The legal transition still opens this exception (partner reported the box lost or damaged). */
  readonly raises?: BoxExceptionType;
}

/** Events with no physical evidence behind them. Out of sequence they are refused, not applied. */
export const ADMINISTRATIVE_EVENTS: readonly BoxEventType[] = [
  'cancelShipment',
  'writeOff',
  'found',
];

const NON_FINAL_STATUSES = BOX_STATUSES.filter((status) => !isFinalStatus(status));

export const TRANSITIONS: readonly TransitionRow[] = [
  { from: 'none', event: 'collect', to: 'at_branch', preconditions: [] },

  {
    from: 'at_branch',
    event: 'loadTrip',
    to: 'on_trip',
    preconditions: ['no_blocking_hold', 'at_event_branch'],
  },
  { from: 'on_trip', event: 'unloadFromTrip', to: 'at_branch', preconditions: ['same_carrier'] },
  {
    from: 'on_trip',
    event: 'receiveTrip',
    to: 'at_branch',
    preconditions: ['same_carrier', 'scan_at_destination'],
  },

  {
    from: 'at_branch',
    event: 'loadContainer',
    to: 'in_container',
    // Any open hold keeps a box out of a container, not just the blocking kinds (D-16, 04 §4.4).
    preconditions: ['paid_in_full', 'no_active_hold', 'at_event_branch'],
  },
  {
    from: 'in_container',
    event: 'removeFromContainer',
    to: 'at_branch',
    preconditions: ['same_carrier'],
  },
  {
    from: 'in_container',
    event: 'exportContainer',
    to: 'exported',
    preconditions: ['same_carrier'],
  },
  { from: 'exported', event: 'containerArrived', to: 'at_port', preconditions: ['same_carrier'] },
  {
    from: 'at_port',
    event: 'receiveContainer',
    to: 'at_branch',
    preconditions: ['same_carrier', 'scan_at_destination'],
  },

  {
    from: 'at_branch',
    event: 'handoverToPartner',
    to: 'with_partner',
    preconditions: ['paid_in_full', 'no_active_hold', 'at_event_branch'],
  },
  {
    from: 'with_partner',
    event: 'partnerUpdate',
    to: 'with_partner',
    preconditions: ['same_carrier'],
    partnerStatuses: [
      'received_by_partner',
      'out_for_delivery',
      'failed_attempt',
      'returned_to_hub',
    ],
  },
  {
    from: 'with_partner',
    event: 'partnerUpdate',
    to: 'delivered',
    preconditions: ['same_carrier'],
    partnerStatuses: ['delivered'],
  },
  {
    from: 'with_partner',
    event: 'partnerUpdate',
    to: 'with_partner',
    preconditions: ['same_carrier'],
    partnerStatuses: ['damaged'],
    raises: 'damaged',
  },
  {
    from: 'with_partner',
    event: 'partnerUpdate',
    to: 'with_partner',
    preconditions: ['same_carrier'],
    partnerStatuses: ['lost'],
    raises: 'missing',
  },
  {
    from: 'with_partner',
    event: 'returnFromPartner',
    to: 'at_branch',
    preconditions: ['same_carrier'],
  },

  {
    from: 'at_branch',
    event: 'startRun',
    to: 'out_for_delivery',
    preconditions: ['no_active_hold', 'at_event_branch'],
  },
  {
    from: 'out_for_delivery',
    event: 'deliverStop',
    to: 'delivered',
    preconditions: ['same_carrier'],
  },
  { from: 'out_for_delivery', event: 'closeRun', to: 'at_branch', preconditions: ['same_carrier'] },

  {
    from: 'at_branch',
    event: 'completeReturn',
    to: 'returned',
    preconditions: ['returning_flag', 'at_event_branch'],
  },
  {
    from: 'at_branch',
    event: 'cancelShipment',
    to: 'cancelled',
    preconditions: ['shipment_not_exported'],
  },
  ...NON_FINAL_STATUSES.map((from): TransitionRow => ({
    from,
    event: 'writeOff',
    to: 'lost',
    preconditions: [],
  })),
  // A missing box turns up at a branch (`resolveException` found). Wherever the books last had it.
  ...NON_FINAL_STATUSES.map((from): TransitionRow => ({
    from,
    event: 'found',
    to: 'at_branch',
    preconditions: [],
  })),
];

function appliesTo(row: TransitionRow, event: BoxEvent): boolean {
  if (row.event !== event.type) return false;
  if (row.partnerStatuses === undefined) return true;
  return event.type === 'partnerUpdate' && row.partnerStatuses.includes(event.partnerStatus);
}

/** The legal row for this event from this state, if there is one. */
export function findTransition(
  from: BoxStatus | 'none',
  event: BoxEvent,
): TransitionRow | undefined {
  return TRANSITIONS.find((row) => row.from === from && appliesTo(row, event));
}

/** Where the event puts the box when it happens, whether or not it was expected. */
export function targetStatus(event: BoxEvent): BoxStatus {
  const row = TRANSITIONS.find((candidate) => appliesTo(candidate, event));
  if (row === undefined) throw new Error(`No transition row for ${event.type}`);
  return row.to;
}

/** The business-rule preconditions of an event type; the same for every row of that type. */
export function refusalPreconditionsFor(type: BoxEventType): RefusalPrecondition[] {
  const row = TRANSITIONS.find((candidate) => candidate.event === type);
  return (row?.preconditions ?? []).filter((p): p is RefusalPrecondition =>
    (REFUSAL_PRECONDITIONS as readonly string[]).includes(p),
  );
}

/** Event types that have a legal row from this state. */
export function legalEventTypes(from: BoxStatus | 'none'): BoxEventType[] {
  return [...new Set(TRANSITIONS.filter((row) => row.from === from).map((row) => row.event))];
}
